import { IGroupedComboBoxItem } from 'grouped-combobox-core';
import { IFont, IFontFamily, IMruEntry } from '../FontPicker/FontPicker.types.shared';
import { FontType, IStringResources, VariantType } from '../reka/FontPicker.types.base';
import { assert } from './assert';
import { canShowIcon } from './fontIcons';
import { isThemeFont } from './fontUtils';
import { canPinFont, getFontAndPinnedState } from './pinning';

type SpecialFontType = FontType | VariantType | 'theme' | undefined;

function getSpecialFontType(font: IFont): SpecialFontType {
    if (isThemeFont(font)) {
        return 'theme';
    }

    const { fontType } = font as IFontFamily;
    if (fontType) {
        return fontType;
    }

    const { variantType } = font as IMruEntry;
    return variantType;
}

/**
 * Return the text used as a description for the PinActionButton.
 * For special fonts, this is also the tooltip text for the PinActionButton
 */
function getSpecialFontDescription(
    font: IFont,
    stringResources: IStringResources,
    isSubscriber: boolean
): string | undefined {
    const itemType = getSpecialFontType(font);

    switch (itemType) {
        case 'theme':
            return stringResources.themeFontTooltip;

        case 'missing':
            return stringResources.missingFontTooltip;

        case 'embedded':
            return stringResources.embeddedFontTooltip;

        case 'premium':
            if (!isSubscriber) {
                return stringResources.premiumFontTooltip;
            }
            break;

        case 'compatible':
            return stringResources.compatibleFontTooltip;
    }
}

/**
 * Return the tooltip text shown when hovering or focused on a font item
 */
export function getFontTooltip(
    font: IFont,
    stringResources: IStringResources,
    isSubscriber: boolean,
    isKeyFocused: boolean,
    allowFontInfoPane: boolean,
    allowPinning: boolean
): string {
    if (!isKeyFocused || !canShowIcon(font, isSubscriber)) {
        return font.name;
    }

    const itemType = getSpecialFontType(font);

    let secondLine: string | undefined = undefined;
    if (
        allowFontInfoPane &&
        (itemType === 'theme' ||
            itemType === 'embedded' ||
            itemType === 'compatible' ||
            (itemType === 'premium' && !isSubscriber))
    ) {
        secondLine = stringResources.fontOtherActionTooltip;
    } else if (allowFontInfoPane && itemType === 'missing') {
        secondLine = stringResources.fontMissingFontTooltip;
    } else if (allowPinning && canPinFont(font, isSubscriber)) {
        secondLine = stringResources.fontPinningTooltip;
    }

    // This means we're not a subscriber and this is not a premium font
    if (!secondLine) {
        return font.name;
    }

    // Added space between strings in case \n doesn't result in a newline
    return `${font.name} \n${secondLine}`;
}

/**
 * Return the tooltip and description text for the PinActionButton.
 * The description is used to describe the selected font for screen reader users.
 */
export function getIconTooltipAndDescription(
    item: IGroupedComboBoxItem,
    stringResources: IStringResources,
    isSubscriber: boolean
): [string, string] | undefined {
    const [font, isPinned] = getFontAndPinnedState(item);
    if (!canShowIcon(font, isSubscriber)) {
        return undefined;
    }

    const specialFontDescription = getSpecialFontDescription(font, stringResources, isSubscriber);
    if (specialFontDescription) {
        return [specialFontDescription, specialFontDescription];
    }

    assert(isSubscriber);

    const { pinnedTooltip, pinnedDescription, unPinnedTooltip, unPinnedDescription } =
        stringResources;

    return isPinned ? [pinnedTooltip, pinnedDescription] : [unPinnedTooltip, unPinnedDescription];
}

/**
 * Returns the action description for screen reader to explain how to invoke the Pin Action Button.
 */
export function getIconActionDescription(
    item: IGroupedComboBoxItem,
    isSubscriber: boolean,
    allowFontInfoPane: boolean,
    allowPinning: boolean,
    stringResources: IStringResources
): string | undefined {
    const [font] = getFontAndPinnedState(item);
    const itemType = getSpecialFontType(font);

    if (
        allowFontInfoPane &&
        (itemType === 'theme' ||
            itemType === 'embedded' ||
            itemType === 'compatible' ||
            itemType === 'missing' ||
            (itemType === 'premium' && !isSubscriber))
    ) {
        return stringResources.fontOtherActionTooltip;
    }

    if (allowPinning && canPinFont(font, isSubscriber)) {
        return stringResources.fontPinningTooltip;
    }

    return undefined;
}
